import axios from 'axios';

const API_URL = 'https://treatment.msgnaturopathy.com/resort/api';

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json'
  }
});

api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.dispatchEvent(new Event('auth:logout'));
    }
    return Promise.reject(error);
  }
);

export const authAPI = {
  login: (credentials) => api.post('/auth/login', credentials)
};

export const patientAPI = {
  register: (data) => api.post('/patients/register', data),
  search: (query) => api.get(`/patients/search?query=${query}`),
  getById: (id) => api.get(`/patients/${id}`),
  getAll: (params) => api.get('/patients/list/all', { params }),
  getPatientDetails: (id) => api.get(`/patients/${id}/details`),
  getDischargeSummary: (admissionId) => api.get(`/patients/admission/${admissionId}/discharge-summary`),
  getTodaysRecord: (patientId) => api.get(`/patients/${patientId}/todays-record`),
  scanAadhar: (imageBase64, mimeType) => api.post('/patients/scan-aadhar', { imageBase64, mimeType }),
  getDailyPlan: (patientId, date) => api.get(`/patients/${patientId}/daily-plan`, { params: { date } }),
  update: (id, data) => api.put(`/patients/${id}`, data)
};

export const visitAPI = {
  create: (data) => api.post('/visits/create', data),
  recordVitals: (visitId, data) => api.post(`/visits/${visitId}/vitals`, data),
  updateVitals: (visitId, data) => api.put(`/visits/${visitId}/vitals`, data),
  recordMedicalHistory: (visitId, data) => api.post(`/visits/${visitId}/medical-history`, data),
  updateMedicalHistory: (visitId, data) => api.put(`/visits/${visitId}/medical-history`, data),
  getPending: () => api.get('/visits/pending'),
  getDetails: (visitId) => api.get(`/visits/${visitId}/details`)
};

export const consultationAPI = {
  create: (data) => api.post('/consultations/create', data),
  getById: (id) => api.get(`/consultations/${id}`),
  getMyConsultations: (params) => api.get('/consultations/my-consultations', { params }),
  getAll: (params) => api.get('/consultations/all', { params }),
  getFollowUps: (consultationId) => api.get(`/consultations/${consultationId}/follow-ups`)
};

export const treatmentAPI = {
  getAll: () => api.get('/treatments'),
  getPackages: () => api.get('/treatments/packages')
};

export const ipdAPI = {
  getAvailableRooms: () => api.get('/ipd/rooms/available'),
  admitPatient: (data) => api.post('/ipd/admit', data),
  dischargePatient: (admissionId, data) => api.post(`/ipd/discharge/${admissionId}`, data),
  extendDischargeDate: (admissionId, data) => api.post(`/ipd/extend-discharge/${admissionId}`, data),
  getActiveAdmissions: () => api.get('/ipd/admissions/active'),
  getAdmissionDetails: (admissionId) => api.get(`/ipd/admissions/${admissionId}/details`),
  updateClinicalNotes: (admissionId, data) => api.put(`/ipd/admissions/${admissionId}/clinical-notes`, data),
  getFormOverrides: (admissionId) => api.get(`/ipd/admissions/${admissionId}/form-overrides`),
  saveFormOverrides: (admissionId, data) => api.put(`/ipd/admissions/${admissionId}/form-overrides`, data),

  // Diet Plan APIs
  getDietItems: () => api.get('/ipd/diet-items'),
  createDietPlan: (admissionId, data) => api.post(`/ipd/${admissionId}/diet-plan`, data),
  getDietPlan: (admissionId, params) => api.get(`/ipd/${admissionId}/diet-plan`, { params }),
  getDietPlanByDate: (admissionId, date) => api.get(`/ipd/${admissionId}/diet-plan/${date}`),
  copyDietDay: (data) => api.post('/ipd/diet-plan/copy-day', data),
  copyDietMeal: (data) => api.post('/ipd/diet-plan/copy-meal', data),
  deleteDietPlan: (planId) => api.delete(`/ipd/diet-plan/${planId}`),

  // Treatment Plan APIs
  createTreatmentPlan: (admissionId, data) => api.post(`/ipd/${admissionId}/treatment-plan`, data),
  getTreatmentPlan: (admissionId, params) => api.get(`/ipd/${admissionId}/treatment-plan`, { params }),
  getTreatmentPlanByDate: (admissionId, date) => api.get(`/ipd/${admissionId}/treatment-plan/${date}`),
  copyTreatmentDay: (data) => api.post('/ipd/treatment-plan/copy-day', data),
  deleteTreatmentPlan: (planId) => api.delete(`/ipd/treatment-plan/${planId}`),

  // Discharge Prescription APIs
  createDischargePrescription: (admissionId, data) => api.post(`/ipd/${admissionId}/discharge-prescription`, data),
  getDischargePrescription: (admissionId) => api.get(`/ipd/${admissionId}/discharge-prescription`),

  // Room change
  changeRoom: (admissionId, data) => api.post(`/ipd/admissions/${admissionId}/change-room`, data)
};

export const dayCareAPI = {
  // Registrations
  register: (data) => api.post('/day-care/register', data),
  getActiveRegistrations: () => api.get('/day-care/registrations/active'),
  getPatientRegistrations: (patientId) => api.get(`/day-care/patient/${patientId}`),
  discharge: (dayCareId) => api.post(`/day-care/discharge/${dayCareId}`),
  extendDuration: (dayCareId, data) => api.post(`/day-care/extend/${dayCareId}`, data),

  // Diet plan — same signatures as ipdAPI so DietPlanManagement can use as apiOverride
  getDietItems: () => api.get('/day-care/diet-items'),
  createDietPlan: (admissionId, data) => api.post(`/day-care/${admissionId}/diet-plan`, data),
  getDietPlan: (admissionId, params) => api.get(`/day-care/${admissionId}/diet-plan`, { params }),
  getDietPlanByDate: (admissionId, date) => api.get(`/day-care/${admissionId}/diet-plan/${date}`),
  copyDietDay: (data) => api.post('/day-care/diet-plan/copy-day', data),
  copyDietMeal: (data) => api.post('/day-care/diet-plan/copy-meal', data),
  deleteDietPlan: (planId) => api.delete(`/day-care/diet-plan/${planId}`),

  // Treatment plan — same signatures as ipdAPI
  createTreatmentPlan: (admissionId, data) => api.post(`/day-care/${admissionId}/treatment-plan`, data),
  getTreatmentPlan: (admissionId, params) => api.get(`/day-care/${admissionId}/treatment-plan`, { params }),
  getTreatmentPlanByDate: (admissionId, date) => api.get(`/day-care/${admissionId}/treatment-plan/${date}`),
  copyTreatmentDay: (data) => api.post('/day-care/treatment-plan/copy-day', data),
  deleteTreatmentPlan: (planId) => api.delete(`/day-care/treatment-plan/${planId}`),

  // Vitals
  recordVitals: (dayCareId, patientId, data) => api.post(`/day-care/vitals/${dayCareId}/${patientId}`, data),
  getVitals: (dayCareId) => api.get(`/day-care/vitals/${dayCareId}`),

  // Registration details, clinical notes & daily plan
  getRegistrationDetails: (dayCareId) => api.get(`/day-care/${dayCareId}/details`),
  updateClinicalNotes: (dayCareId, data) => api.put(`/day-care/${dayCareId}/clinical-notes`, data),
  getDailyPlan: (dayCareId, date) => api.get(`/day-care/${dayCareId}/daily-plan`, { params: { date } }),
};

export const dashboardAPI = {
  getDoctorDashboard: () => api.get('/dashboard/doctor'),
  getDischargingPatients: (date) => api.get('/dashboard/discharging-patients', { params: { date } })
};

export const vitalsAPI = {
  recordVitals: (admissionId, patientId, data) => api.post(`/vitals/${admissionId}/${patientId}`, data),
  getPatientVitals: (admissionId, params) => api.get(`/vitals/${admissionId}`, { params }),
  getVitalsByDate: (admissionId, date) => api.get(`/vitals/${admissionId}/${date}`),
  updateVitals: (vitalId, data) => api.put(`/vitals/record/${vitalId}`, data),
  deleteVitals: (vitalId) => api.delete(`/vitals/record/${vitalId}`)
};

export const dietCoordinatorAPI = {
  getTodaysMeals: (date) => api.get('/diet-coordinator/todays-meals', { params: { date } }),
  markMealDelivered: (mealId, data) => api.patch(`/diet-coordinator/meals/${mealId}/deliver`, data),
  bulkDeliver: (data) => api.patch('/diet-coordinator/meals/bulk-deliver', data),
  getTodaysStats: (date) => api.get('/diet-coordinator/todays-stats', { params: { date } }),
  getDashboard: (date) => api.get('/diet-coordinator/dashboard', { params: { date } })
};

export const treatmentCoordinatorAPI = {
  getTodaysTreatments: (date) => api.get('/treatment-coordinator/todays-treatments', { params: { date } }),
  assignTreatment: (treatmentPlanItemId, data) => api.patch(`/treatment-coordinator/treatment-items/${treatmentPlanItemId}/assign`, data),
  bulkAssign: (data) => api.patch('/treatment-coordinator/treatment-items/bulk-assign', data),
  initiateTreatment: (executionId) => api.patch(`/treatment-coordinator/executions/${executionId}/initiate`),
  completeTreatment: (executionId, data) => api.patch(`/treatment-coordinator/executions/${executionId}/complete`, data),
  bulkUpdateStatus: (data) => api.patch('/treatment-coordinator/executions/bulk-status', data),
  directCompleteTreatment: (treatmentPlanItemId, data) => api.post(`/treatment-coordinator/treatment-items/${treatmentPlanItemId}/complete-direct`, data),
  getTodaysStats: (date) => api.get('/treatment-coordinator/todays-stats', { params: { date } }),
  getAvailableStaff: () => api.get('/treatment-coordinator/available-staff'),
  getDashboard: (date) => api.get('/treatment-coordinator/dashboard', { params: { date } }),

  // OPD Treatment APIs
  getOPDTreatments: (params) => api.get('/treatment-coordinator/opd-treatments', { params }),
  assignOPDTreatment: (prescribedTreatmentId, data) => api.post(`/treatment-coordinator/opd-treatments/${prescribedTreatmentId}/assign`, data),
  bulkAssignOPD: (data) => api.patch('/treatment-coordinator/opd-treatments/bulk-assign', data),
  updateOPDAssignmentStatus: (assignmentId, data) => api.patch(`/treatment-coordinator/opd-assignments/${assignmentId}/status`, data),
  bulkUpdateOPDStatus: (data) => api.patch('/treatment-coordinator/opd-assignments/bulk-status', data),
  getOPDStatistics: () => api.get('/treatment-coordinator/opd-statistics')
};

export const therapistAPI = {
  getAll: (params) => api.get('/therapists', { params }),
  getById: (id) => api.get(`/therapists/${id}`),
  create: (data) => api.post('/therapists', data),
  update: (id, data) => api.put(`/therapists/${id}`, data),
  delete: (id) => api.delete(`/therapists/${id}`),
  getCalendar: (params) => api.get('/therapists/calendar/view', { params }),
  getAvailable: (params) => api.get('/therapists/available/search', { params }),
  bookSlot: (data) => api.post('/therapists/calendar/book', data),
  cancelSlot: (id) => api.delete(`/therapists/calendar/${id}`)
};

export const testReportsAPI = {
  uploadReports: (formData) => api.post('/test-reports/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  getPatientReports: (patientId) => api.get(`/test-reports/patient/${patientId}`),
  getAdmissionReports: (admissionId) => api.get(`/test-reports/admission/${admissionId}`),
  getReportsSummary: (patientId) => api.get(`/test-reports/patient/${patientId}/summary`),
  downloadReport: (reportId) => api.get(`/test-reports/${reportId}/download`, {
    responseType: 'blob'
  }),
  updateReport: (reportId, data) => api.put(`/test-reports/${reportId}`, data),
  deleteReport: (reportId) => api.delete(`/test-reports/${reportId}`)
};

export const adminAPI = {
  // Treatments
  getTreatments: () => api.get('/admin/treatments'),
  createTreatment: (data) => api.post('/admin/treatments', data),
  updateTreatment: (id, data) => api.put(`/admin/treatments/${id}`, data),
  deleteTreatment: (id) => api.delete(`/admin/treatments/${id}`),
  downloadTreatmentsCSV: () => api.get('/admin/treatments/csv', { responseType: 'blob' }),
  uploadTreatmentsCSV: (csv) => api.post('/admin/treatments/csv', { csv }),

  // Packages
  getPackages: () => api.get('/admin/packages'),
  createPackage: (data) => api.post('/admin/packages', data),
  updatePackage: (id, data) => api.put(`/admin/packages/${id}`, data),
  deletePackage: (id) => api.delete(`/admin/packages/${id}`),
  downloadPackagesCSV: () => api.get('/admin/packages/csv', { responseType: 'blob' }),
  uploadPackagesCSV: (csv) => api.post('/admin/packages/csv', { csv }),

  // Room Categories
  getRoomCategories: () => api.get('/admin/room-categories'),
  createRoomCategory: (data) => api.post('/admin/room-categories', data),
  updateRoomCategory: (id, data) => api.put(`/admin/room-categories/${id}`, data),
  deleteRoomCategory: (id) => api.delete(`/admin/room-categories/${id}`),
  downloadRoomCategoriesCSV: () => api.get('/admin/room-categories/csv', { responseType: 'blob' }),
  uploadRoomCategoriesCSV: (csv) => api.post('/admin/room-categories/csv', { csv }),

  // Rooms
  getRooms: () => api.get('/admin/rooms'),
  createRoom: (data) => api.post('/admin/rooms', data),
  updateRoom: (id, data) => api.put(`/admin/rooms/${id}`, data),
  deleteRoom: (id) => api.delete(`/admin/rooms/${id}`),
  downloadRoomsCSV: () => api.get('/admin/rooms/csv', { responseType: 'blob' }),
  uploadRoomsCSV: (csv) => api.post('/admin/rooms/csv', { csv }),

  // Diet Items
  getDietItems: () => api.get('/admin/diet-items'),
  createDietItem: (data) => api.post('/admin/diet-items', data),
  updateDietItem: (id, data) => api.put(`/admin/diet-items/${id}`, data),
  deleteDietItem: (id) => api.delete(`/admin/diet-items/${id}`),
  downloadDietItemsCSV: () => api.get('/admin/diet-items/csv', { responseType: 'blob' }),
  uploadDietItemsCSV: (csv) => api.post('/admin/diet-items/csv', { csv }),

  // Users
  getUsers: () => api.get('/admin/users'),
  createUser: (data) => api.post('/admin/users', data),
  updateUser: (id, data) => api.put(`/admin/users/${id}`, data),
  deleteUser: (id) => api.delete(`/admin/users/${id}`),
  downloadUsersCSV: () => api.get('/admin/users/csv', { responseType: 'blob' }),
  uploadUsersCSV: (csv) => api.post('/admin/users/csv', { csv })
};

export const servicePackagesAPI = {
  getPlans: (type) => api.get('/service-packages/plans', { params: type ? { type } : {} }),
  purchasePackage: (data) => api.post('/service-packages/purchase', data),
  getPatientPackages: (patientId) => api.get(`/service-packages/patient/${patientId}`),
  getPatientBenefits: (patientId) => api.get(`/service-packages/patient/${patientId}/benefits`)
};

export const billingAPI = {
  checkConsultationFee: (patientId) => api.get(`/billing/check-consultation-fee/${patientId}`),
  getBillingOptions: () => api.get('/billing/options'),
  getIPDBillingData: (admissionId) => api.get(`/billing/ipd-data/${admissionId}`),
  generateBill: (data) => api.post('/billing/generate', data),
  getBillById: (billId) => api.get(`/billing/${billId}`),
  getBillWithPayments: (billId) => api.get(`/billing/${billId}/payments`),
  getPatientBills: (patientId) => api.get(`/billing/patient/${patientId}`),
  getAllBills: (params) => api.get('/billing/all', { params }),
  updateBillStatus: (billId, data) => api.patch(`/billing/${billId}/status`, data),
  // Edit bill
  editBill: (billId, data) => api.put(`/billing/${billId}/edit`, data),
  // Payments
  recordPayment: (data) => api.post('/billing/payments', data),
  editPayment: (paymentId, data) => api.put(`/billing/payments/${paymentId}/edit`, data),
  getAllPayments: (params) => api.get('/billing/payments/all', { params }),
  // Refunds
  processRefund: (billId, data) => api.post(`/billing/${billId}/refund`, data),
};

export const prognosisAPI = {
  recordIPD: (admissionId, patientId, data) => api.post(`/prognosis/ipd/${admissionId}/${patientId}`, data),
  getIPD: (admissionId) => api.get(`/prognosis/ipd/${admissionId}`),
  recordDayCare: (dayCareId, patientId, data) => api.post(`/prognosis/day-care/${dayCareId}/${patientId}`, data),
  getDayCare: (dayCareId) => api.get(`/prognosis/day-care/${dayCareId}`)
};

export default api;
